/**
 * Community trending score — shared by tribes and forums.
 *
 * The old badge logic was a raw counter comparison: forums needed
 * `trendingScore > 100` while tribes needed `> 0`. Because `trendingScore` only
 * ever incremented (+5 per join), the forum bar meant ~20 joins — unreachable at
 * current scale, so the badge never appeared — while the tribe bar meant a
 * single join, so it appeared on nearly everything. Same badge, opposite
 * failure modes, and neither one said anything about whether a community was
 * actually alive.
 *
 * This scores three things instead, all as RATES so a big-but-dormant group
 * doesn't outrank a small-but-active one:
 *
 *   joinRate       members gained per week (people are showing up)
 *   postRate       posts per week          (people are talking)
 *   engagement     replies per post        (people are talking to each other)
 *
 * ...then decays the whole thing by how long the group has been quiet, so
 * trending always means "right now" rather than "was busy once".
 *
 * Selection is RELATIVE (see buildTrendingSet): the badge goes to the top slice
 * of whatever is on screen, above a small absolute floor. That's deliberate —
 * an absolute threshold has to be re-tuned every time the app's volume changes,
 * and that re-tuning is exactly what nobody remembers to do.
 */

export interface TrendingCommunity {
    id?: string;
    memberCount?: number;
    postCount?: number;
    commentCount?: number;
    posts7d?: number;
    comments7d?: number;
    createdAt?: any;
    lastActivityAt?: any;
}

const MS_PER_DAY = 24 * 60 * 60 * 1000;
const MS_PER_WEEK = 7 * MS_PER_DAY;

/** Firestore Timestamp | ISO string | Date | millis → millis (0 if unusable). */
function toMillis(value: any): number {
    if (!value) return 0;
    if (typeof value.toMillis === 'function') return value.toMillis();
    if (typeof value.toDate === 'function') return value.toDate().getTime();
    if (typeof value === 'number') return value;
    const parsed = new Date(value).getTime();
    return Number.isNaN(parsed) ? 0 : parsed;
}

/**
 * Weekly rates + a freshness multiplier. Higher is hotter; 0 means "no signal".
 */
export function computeCommunityTrendingScore(group: TrendingCommunity): number {
    const members = group.memberCount ?? 0;
    const posts = group.postCount ?? 0;
    const comments = group.commentCount ?? 0;

    // Age in weeks, floored at 1 so a brand-new group isn't divided by ~0 and
    // rocketed to the top of the list on its first post.
    const createdMs = toMillis(group.createdAt);
    const ageWeeks = createdMs > 0
        ? Math.max(1, (Date.now() - createdMs) / MS_PER_WEEK)
        : 1;

    // 1. Are people joining? Members per week since creation.
    const joinRate = members / ageWeeks;

    // 2. Are people posting? Prefer the rolling 7-day counter when the nightly
    //    rollup has populated it; otherwise fall back to a lifetime average.
    const postRate = typeof group.posts7d === 'number' && group.posts7d > 0
        ? group.posts7d
        : posts / ageWeeks;

    // 3. Are people replying to each other? Replies per post — a quality signal
    //    that's independent of raw size.
    const engagementBase = typeof group.comments7d === 'number' && group.comments7d > 0
        ? group.comments7d
        : comments;
    const engagementDenom = typeof group.posts7d === 'number' && group.posts7d > 0
        ? group.posts7d
        : posts;
    const engagementPerPost = engagementDenom > 0 ? engagementBase / engagementDenom : 0;

    // Quiet groups fade out over two weeks rather than dropping off a cliff.
    const lastActivityMs = toMillis(group.lastActivityAt);
    const daysQuiet = lastActivityMs > 0 ? (Date.now() - lastActivityMs) / MS_PER_DAY : 14;
    const freshness = Math.max(0.1, Math.min(1, 1 - daysQuiet / 14));

    // Posting is weighted highest (it's what makes a community worth opening),
    // engagement next (it's what makes people come back), joins last (easiest
    // to inflate).
    const raw = (postRate * 3) + (engagementPerPost * 2) + (joinRate * 1.5);

    return raw * freshness;
}

// A group must clear this before it can be badged at all, no matter how quiet
// the rest of the list is — otherwise "the least dead group" wins a badge.
export const MIN_TRENDING_SCORE = 3;

// Never badge more than this many at once; a badge on everything is not a badge.
const MAX_TRENDING = 5;

// Top slice of the list that's eligible.
const TRENDING_PERCENTILE = 0.2;

/**
 * Given the groups currently being rendered, returns the ids that should show
 * the Trending badge: the top ~20% by score, capped at 5, floor-gated.
 *
 * Self-calibrating — works the same whether the list has 6 groups or 600.
 */
export function buildTrendingSet(groups: TrendingCommunity[]): Set<string> {
    const scored = groups
        .filter(g => g && g.id)
        .map(g => ({ id: String(g.id), score: computeCommunityTrendingScore(g) }))
        .filter(g => g.score >= MIN_TRENDING_SCORE)
        .sort((a, b) => b.score - a.score);

    if (scored.length === 0) return new Set<string>();

    // At least one badge once anything clears the floor, so a small-but-healthy
    // list isn't left with none.
    const slots = Math.max(1, Math.min(MAX_TRENDING, Math.ceil(scored.length * TRENDING_PERCENTILE)));

    return new Set(scored.slice(0, slots).map(g => g.id));
}
